import { alignmentsAtom } from '@/atoms/alignment.atom';
import { stationVolumePreviewAtom } from '@/atoms/stationVolumes.atom';
import { HIGHLIGHT } from '@/components/3D/AlignmentOverlay';
import { buildSwathGeometry, buildSwathOutline } from '@/lib/stationVolumes/swathGeometry';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import * as THREE from 'three';

/**
 * Dialog-time swath ribbon: 25% translucent amber wash + a single-outline
 * silhouette, driven by `stationVolumePreviewAtom` (written by
 * `RunSurfaceDifferenceDialog` while its station-volumes section is open,
 * cleared on close/unmount).
 *
 * Geometry sampling is shared with Task 14's persisted 3D bands via
 * `buildSwathGeometry`/`buildSwathOutline` (`@/lib/stationVolumes/swathGeometry`)
 * — no second copy of the loop lives here (T9.1). The outline is drawn as a
 * `THREE.LineLoop` rather than `wireframeGeometry` (fix round 1, item 6):
 * `wireframeGeometry` draws every triangle edge, which reads as a dense
 * cross-hatch on a corridor built from ~1 000 quads — wrong on screen even
 * though it was the plan's original instruction.
 */
export const SwathPreview = () => {
  const preview = useAtomValue(stationVolumePreviewAtom);
  const alignments = useAtomValue(alignmentsAtom);
  const alignment = preview ? alignments.get(preview.key) : undefined;

  const origin = useMemo(
    () =>
      alignment
        ? new THREE.Vector3(alignment.points[0], alignment.points[1], alignment.points[2])
        : new THREE.Vector3(),
    [alignment],
  );

  // Fine enough that a 100 m-radius curve reads as a curve, cheap enough to
  // rebuild on every keystroke (a 5 km corridor is ~1000 samples).
  const { fromStation, toStation, step } = useMemo(() => {
    if (!alignment || !preview) return { fromStation: 0, toStation: 0, step: 1 };
    const from = preview.from ?? 0;
    const to = preview.to ?? alignment.length;
    return { fromStation: from, toStation: to, step: Math.min(5, Math.max(1, (to - from) / 1000)) };
  }, [alignment, preview]);

  const geometry = useMemo(() => {
    if (!alignment || !preview) return null;
    return buildSwathGeometry(alignment, preview.swathLeft, preview.swathRight, fromStation, toStation, step, origin);
  }, [alignment, preview, origin, fromStation, toStation, step]);

  const outline = useMemo(() => {
    if (!alignment || !preview) return null;
    const geom = buildSwathOutline(alignment, preview.swathLeft, preview.swathRight, fromStation, toStation, step, origin);
    const mat = new THREE.LineBasicMaterial({ color: HIGHLIGHT, depthTest: false, transparent: true, opacity: 0.85 });
    const obj = new THREE.LineLoop(geom, mat);
    obj.renderOrder = 1000;
    obj.frustumCulled = false;
    return obj;
  }, [alignment, preview, origin, fromStation, toStation, step]);

  useEffect(() => () => geometry?.dispose(), [geometry]);
  useEffect(() => {
    if (!outline) return;
    return () => {
      outline.geometry.dispose();
      (outline.material as THREE.Material).dispose();
    };
  }, [outline]);

  if (!geometry || !outline) return null;

  return (
    <group position={origin}>
      <mesh geometry={geometry} renderOrder={3} raycast={() => null}>
        <meshBasicMaterial
          color={HIGHLIGHT}
          transparent
          opacity={0.25}
          side={THREE.DoubleSide}
          depthWrite={false}
          depthTest={false}
        />
      </mesh>
      <primitive object={outline} />
    </group>
  );
};
